import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
} from '@radix-ui/react-icons';
import {
  Box,
  Button,
  Card,
  Flex,
  Grid,
  Heading,
  IconButton,
} from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { MachineContext } from 'contexts/machine.context';
import { t } from 'i18next';
import { numberToImperial } from 'lib_ts/classes/math.utilities';
import { StaticVideoType } from 'lib_ts/enums/machine-msg.enum';
import { HardwareVersion, MS_LIMITS } from 'lib_ts/enums/machine.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IImperialCoordinate } from 'lib_ts/interfaces/i-imperial';
import { DEFAULT_MACHINE_STATE } from 'lib_ts/interfaces/i-machine-state';
import { IMachineStateMsg } from 'lib_ts/interfaces/machine-msg/i-machine-state';
import { IPosition } from 'lib_ts/interfaces/pitches/i-base';
import { clamp } from 'lodash';
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Slider from 'react-input-slider';
import useMiscStore from 'stores/use-misc-store';
import './index.scss';

interface IPositionDetails {
  header: string;
  decimal: string;
  imperial: string;
  adjust: {
    axis: 'x' | 'y';
    increaseIcon: JSX.Element;
    decreaseIcon: JSX.Element;
  };
}

const THUMB_HT_PX = 320;

const ENABLE_KEYBOARD_AJUSTMENT = false;
const ENABLE_BUTTON_ADJUSTMENT = false;

/** for adjusting machine position via keyboard input */
const DIR_Y_INC = 'ArrowUp';
const DIR_Y_DEC = 'ArrowDown';
const DIR_X_DEC = 'ArrowLeft';
const DIR_X_INC = 'ArrowRight';

const DIR_KEY_CODES = [DIR_Y_DEC, DIR_Y_INC, DIR_X_DEC, DIR_X_INC];

/** given in inches */
const DIR_PRECISION_INCHES = {
  /** i.e. 0.01 feet per press */
  CTRL_SHIFT: 0.12,
  CTRL: 1,
  SHIFT: 3,
};

const BOUNDARY = {
  /** governs overall boundary for slider */
  SLIDER_X: {
    MIN: -6,
    MAX: 6,
    DEFAULT: 0,
    STEP: 0.1,
  },
  /** governs selectable values within slider */
  VALID_X: MS_LIMITS.POSITION.X,
  /** governs overall boundary for slider */
  SLIDER_Y: {
    MIN: 0,
    MAX: 9,
    DEFAULT: 4.2,
    STEP: 0.1,
  },
  /** governs selectable values within slider */
  VALID_Y: MS_LIMITS.POSITION.Z,
};

type NumberPosXZ = Omit<IPosition, 'py'>;

interface StringPosXZ {
  px: IImperialCoordinate;
  pz: IImperialCoordinate;
}

interface IProps {
  onUpdate?: (pos: NumberPosXZ) => void;
}

export const MachinePositionView: FC<IProps> = (props) => {
  const anyOpenDialogs = useMiscStore((s) => s.anyOpenDialogs);

  const { loading, machine, lastMS, sendRawTarget } =
    useContext(MachineContext);

  const [slider, setSlider] = useState<NumberPosXZ>({
    px: lastMS?.px ?? DEFAULT_MACHINE_STATE.px,
    pz: lastMS?.pz ?? DEFAULT_MACHINE_STATE.pz,
  });

  useEffect(() => {
    props.onUpdate?.(slider);
  }, [slider]);

  const position = useMemo<StringPosXZ>(
    () => ({
      px: numberToImperial(slider.px),
      pz: numberToImperial(slider.pz),
    }),
    [slider]
  );

  const imageDir = useMemo(() => {
    switch (machine.hardware_version) {
      case HardwareVersion.Arc2022:
        return '/img/wireframes/Arc 2022';

      default:
        return '/img/wireframes/Arc 2023';
    }
  }, [machine.hardware_version]);

  const positionDetails: IPositionDetails[] = [
    {
      header: 'SIDE',
      imperial: `${position.px.sign === -1 ? '-' : ''} ${position.px.ft}' ${
        position.px.in
      }"`,
      decimal: `${position.px.originalFt.toFixed(2)} ft`,
      adjust: {
        axis: 'x',
        increaseIcon: <ArrowRightIcon />,
        decreaseIcon: <ArrowLeftIcon />,
      },
    },
    {
      header: 'HEIGHT',
      imperial: `${position.pz.sign === -1 ? '-' : ''} ${position.pz.ft}' ${
        position.pz.in
      }"`,
      decimal: `${position.pz.originalFt.toFixed(2)} ft`,
      adjust: {
        axis: 'y',
        increaseIcon: <ArrowUpIcon />,
        decreaseIcon: <ArrowDownIcon />,
      },
    },
  ];

  const adjustPosition = useCallback((axis: string, amount: number) => {
    switch (axis) {
      case 'x': {
        setSlider((prev) => ({
          ...prev,
          px: clamp(
            prev.px + amount,
            BOUNDARY.VALID_X.MIN,
            BOUNDARY.VALID_X.MAX
          ),
        }));
        break;
      }

      case 'y': {
        setSlider((prev) => ({
          ...prev,
          pz: clamp(
            prev.pz + amount,
            BOUNDARY.VALID_Y.MIN,
            BOUNDARY.VALID_Y.MAX
          ),
        }));
        break;
      }

      default: {
        break;
      }
    }
  }, []);

  // listen to keyboard adjustments
  useEffect(() => {
    if (!ENABLE_KEYBOARD_AJUSTMENT) {
      return;
    }

    if (anyOpenDialogs) {
      return;
    }

    const callback = (event: KeyboardEvent) => {
      if (!event.ctrlKey && !event.shiftKey) {
        return;
      }

      if (!DIR_KEY_CODES.includes(event.code)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (event.repeat) {
        return;
      }

      /** in feet */
      const deltaFt: number = (() => {
        if (event.ctrlKey && event.shiftKey) {
          return DIR_PRECISION_INCHES.CTRL_SHIFT / 12;
        }

        if (event.ctrlKey) {
          /** precise */
          return DIR_PRECISION_INCHES.CTRL / 12;
        }

        if (event.shiftKey) {
          /** rough */
          return DIR_PRECISION_INCHES.SHIFT / 12;
        }

        return 0;
      })();

      switch (event.code) {
        case DIR_X_INC: {
          adjustPosition('x', deltaFt);
          return;
        }

        case DIR_X_DEC: {
          adjustPosition('x', -deltaFt);
          return;
        }
        case DIR_Y_INC: {
          adjustPosition('y', deltaFt);
          return;
        }

        case DIR_Y_DEC: {
          adjustPosition('y', -deltaFt);
          return;
        }

        default: {
          return;
        }
      }
    };

    document.addEventListener('keydown', callback);

    return () => {
      document.removeEventListener('keydown', callback);
    };
  }, []);

  // clamps values to what's allowed by boundaries
  const handleChange = (values: NumberPosXZ) =>
    setSlider({
      px: clamp(values.px, BOUNDARY.VALID_X.MIN, BOUNDARY.VALID_X.MAX),
      pz: clamp(values.pz, BOUNDARY.VALID_Y.MIN, BOUNDARY.VALID_Y.MAX),
    });

  return (
    <ErrorBoundary identifier="MachinePositionView">
      <div className="MachineView">
        <Grid columns="5" gap={RADIX.FLEX.GAP.LG}>
          <Box gridColumn="span 4">
            <div className="sizer">
              <div className="slider-wrapper">
                <Slider
                  axis="xy"
                  xstep={BOUNDARY.SLIDER_X.STEP}
                  xmin={BOUNDARY.SLIDER_X.MIN}
                  xmax={BOUNDARY.SLIDER_X.MAX}
                  x={slider.px}
                  ystep={BOUNDARY.SLIDER_Y.STEP}
                  ymin={BOUNDARY.SLIDER_Y.MIN}
                  ymax={BOUNDARY.SLIDER_Y.MAX}
                  y={slider.pz}
                  onChange={(v) => handleChange({ px: v.x, pz: v.y })}
                  styles={{
                    track: {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      // backgroundImage: `url("${imageDir}/hdpe-sides.png")`,
                      // backgroundPosition: 'center',
                      // backgroundSize: 'contain',
                      // backgroundRepeat: 'no-repeat',
                      width: 'auto',
                      height: '100%',
                    },
                    thumb: {
                      backgroundImage: `url("${imageDir}/hdpe-center-alt.png")`,
                      backgroundColor: 'rgba(0, 255, 0, 0)',
                      backgroundPosition: 'center',
                      backgroundSize: 'contain',
                      backgroundRepeat: 'no-repeat',
                      width: `${THUMB_HT_PX * 0.8}px`,
                      height: `${THUMB_HT_PX}px`,
                      boxShadow: 'none',
                      opacity: 1,
                    },
                  }}
                  yreverse
                />
              </div>
            </div>
          </Box>

          <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
            <Box pl={RADIX.BOX.PAD.MD}>
              <ul className="no-style no-padding">
                {positionDetails.map((item, index) => (
                  <li key={index}>
                    {item.header}: {item.imperial} ({item.decimal})
                  </li>
                ))}
              </ul>
            </Box>
            <Heading size={RADIX.HEADING.SIZE.MD}>Controls</Heading>
            <Button
              disabled={loading}
              onClick={() => {
                handleChange({
                  px: lastMS?.px ?? DEFAULT_MACHINE_STATE.px,
                  pz: lastMS?.pz ?? DEFAULT_MACHINE_STATE.pz,
                });
              }}
            >
              Reset (Current)
            </Button>

            <Button
              disabled={loading}
              onClick={() => {
                handleChange({
                  px: DEFAULT_MACHINE_STATE.px,
                  pz: DEFAULT_MACHINE_STATE.pz,
                });
              }}
            >
              Reset (Default)
            </Button>

            <Button
              color={RADIX.COLOR.SEND_PITCH}
              disabled={loading}
              onClick={() => {
                const msTarget: IMachineStateMsg = {
                  ...DEFAULT_MACHINE_STATE,
                  hash: null,
                  force: null,
                  jitter: null,
                  pitch_info: null,
                  px: slider.px,
                  pz: slider.pz,
                  video_uuid: StaticVideoType.screensaver,
                };

                sendRawTarget(msTarget, 'MachinePositionView', false);
              }}
            >
              {t('common.load-pitch')}
            </Button>

            {ENABLE_BUTTON_ADJUSTMENT && (
              <Flex gap={RADIX.FLEX.GAP.SM}>
                {positionDetails.map((item, index) => (
                  <Flex
                    key={`pos-details-${index}`}
                    direction="column"
                    align="center"
                  >
                    <Heading size={RADIX.HEADING.SIZE.SM}>
                      {item.header}
                    </Heading>

                    <Card>
                      <Grid columns="2" gap={RADIX.FLEX.GAP.SM} align="center">
                        <Box gridColumn="span 2">
                          <Heading size={RADIX.HEADING.SIZE.XS}>
                            {item.imperial}
                          </Heading>
                        </Box>

                        <Box gridColumn="span 2">
                          <Heading size={RADIX.HEADING.SIZE.XS}>
                            {item.decimal}
                          </Heading>
                        </Box>

                        <Box>
                          <IconButton
                            variant="soft"
                            onClick={() =>
                              adjustPosition(item.adjust.axis, -0.01)
                            }
                          >
                            {item.adjust.decreaseIcon}
                          </IconButton>
                        </Box>

                        <Box>
                          <IconButton
                            variant="soft"
                            onClick={() =>
                              adjustPosition(item.adjust.axis, 0.01)
                            }
                          >
                            {item.adjust.increaseIcon}
                          </IconButton>
                        </Box>
                      </Grid>
                    </Card>
                  </Flex>
                ))}
              </Flex>
            )}
          </Flex>
        </Grid>
      </div>
    </ErrorBoundary>
  );
};
